import React, { useState } from 'react';
import { useGetAuditLogs } from '@openapi/logs/logs';
import useNotify from '../../hooks/useNotify';
import AuditLogsTable from './components/AuditLogsTable';
import useSorting from '../../hooks/useSorting';
import PageWithTitle from '../../components/atom/PageWithTitle';
import { AUDIT_LOG_DESCRIPTIONS, TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, Stack, TextField } from '@mui/material';

const AuditLogsPage = () => {
    const { t } = useTranslation();
    const { notifyError } = useNotify();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['created_at', 'DESC']);
    const [logType, setLogType] = useState('');
    const [bookingId, setBookingId] = useState('');

    // Options sourced from AUDIT_LOG_DESCRIPTIONS so the dropdown stays in sync
    // with the documented log_types — anything emitted at runtime that isn't
    // here renders as "Unknown log type" on the table anyway, so this is the
    // right source of truth for the filter UI.
    const logTypeOptions = Object.entries(AUDIT_LOG_DESCRIPTIONS)
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const selectedLogTypeOption = logTypeOptions.find((opt) => opt.value === logType) ?? null;

    const {
        data,
        isLoading: loading,
        _error,
    } = useGetAuditLogs(
        {
            logType: logType || undefined,
            bookingId: bookingId ? Number(bookingId) : undefined,
            limit,
            offset: page * limit,
            sort,
            sortDir,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    return (
        <PageWithTitle
            title={t('auditlogs.title')}
            stackHeadline
            headline={[
                <Stack
                    key="headline"
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="flex-end"
                    sx={{ width: '100%' }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: { xs: 0, md: 320 },
                            width: { xs: '100%', md: 'auto' },
                        }}
                    >
                        <Autocomplete
                            fullWidth
                            options={logTypeOptions}
                            value={selectedLogTypeOption}
                            onChange={(_e, next) => {
                                setLogType(next?.value ?? '');
                                setParams({ limit, page: 0 });
                            }}
                            getOptionLabel={(opt) => opt.label}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('auditlogs.columns.logType')}
                                    placeholder={t(
                                        'auditlogs.filterByLogType',
                                        'Filter by log type',
                                    )}
                                    fullWidth
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: 180 } }}>
                        <TextField
                            label={t('auditlogs.filterByBookingId', 'Booking ID')}
                            type="number"
                            value={bookingId}
                            onChange={(e) => {
                                setBookingId(e.target.value);
                                setParams({ limit, page: 0 });
                            }}
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                        />
                    </Box>
                </Stack>,
            ]}
        >
            <AuditLogsTable
                rows={data?.auditlogs}
                limit={limit}
                page={page}
                setParams={setParams}
                total={data?.total}
                loading={loading}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
            />
        </PageWithTitle>
    );
};

export default AuditLogsPage;
